/**
 * Color utility functions for hosted pages
 * Provides WCAG-compliant contrast calculations and color manipulation
 */

/**
 * Calculate the appropriate contrast color (black or white) for a given background color
 * Uses WCAG AA standard for accessibility compliance
 * 
 * @param {string} color - Hex color string (with or without #, supports 3 or 6 character hex)
 * @returns {string} - Returns '#000000' for light backgrounds, '#FFFFFF' for dark backgrounds
 */
export function getContrastColor(color) {
  if (!color) return '#000000';
  
  // Remove # if present and handle 3-char hex codes
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  
  // Convert to RGB
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  
  // Calculate relative luminance using WCAG formula
  const getLuminance = (value) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };
  
  const rLum = getLuminance(r);
  const gLum = getLuminance(g);
  const bLum = getLuminance(b);
  
  // Calculate relative luminance
  const luminance = 0.2126 * rLum + 0.7152 * gLum + 0.0722 * bLum;
  
  // Use WCAG AA standard threshold (0.179 for normal text)
  // Returns black for light backgrounds, white for dark backgrounds
  return luminance > 0.179 ? '#000000' : '#FFFFFF';
}

/**
 * Convert hex color to RGB values
 * 
 * @param {string} hex - Hex color string (with or without #)
 * @returns {object} - Object with r, g, b properties
 */
export function hexToRgb(hex) {
  // Remove # if present and handle 3-char hex codes
  let cleanHex = hex.replace('#', '');
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(char => char + char).join('');
  }
  
  return {
    r: parseInt(cleanHex.slice(0, 2), 16),
    g: parseInt(cleanHex.slice(2, 4), 16),
    b: parseInt(cleanHex.slice(4, 6), 16)
  };
}

/**
 * Calculate relative luminance of a color according to WCAG guidelines
 * 
 * @param {string} color - Hex color string
 * @returns {number} - Relative luminance value between 0 and 1
 */
export function getRelativeLuminance(color) {
  const { r, g, b } = hexToRgb(color);
  
  const getLuminance = (value) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };
  
  const rLum = getLuminance(r);
  const gLum = getLuminance(g);
  const bLum = getLuminance(b);
  
  return 0.2126 * rLum + 0.7152 * gLum + 0.0722 * bLum;
}

/**
 * Calculate contrast ratio between two colors
 * 
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @returns {number} - Contrast ratio (1-21)
 */
export function getContrastRatio(color1, color2) {
  const lum1 = getRelativeLuminance(color1);
  const lum2 = getRelativeLuminance(color2);
  
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  
  return (brightest + 0.05) / (darkest + 0.05);
}
